import { useUnits } from "../../utils/useUnits";
import { measureEm, SANS, MONO } from "../../utils/textMetrics";
import { enabledSlots, legendLayout, legendQtyText, legendFontFeet } from "../../utils/takeoffLegend";

// The legend as PART OF THE DRAWING (utils/takeoffLegend.js): an SVG group in drawing space (world feet), rendered inside
// the canvas's pan / zoom group so it scales with the sheet and is in any snapshot, screenshot or printout of the markup.
// It is deliberately a light "paper" block (white, dark text) in both the light and dark views — it is a drawing
// element that gets printed, not interface. Drag it to place it (when no drawing tool is armed).

export default function TakeoffLegend({ entries, legend, ftPerInch, defaultPos, pxPerFt, draggable, onMove, onMoveEnd }) {
  const units = useUnits();
  const fs = legendFontFeet(legend.fontPt, ftPerInch);
  const slots = enabledSlots(legend);
  const L = legendLayout(entries, slots, fs, measureEm, units);
  const pos = legend.pos || defaultPos;

  const startDrag = (e) => {
    if (!draggable || e.button > 0) return;
    e.stopPropagation(); e.preventDefault();
    const start = { sx: e.clientX, sy: e.clientY, x: pos.x, y: pos.y };
    let last = pos;
    const move = (ev) => { last = { x: start.x + (ev.clientX - start.sx) / pxPerFt, y: start.y + (ev.clientY - start.sy) / pxPerFt }; onMove(last); };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); onMoveEnd(last); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  const ink = "#111827", faint = "#6b7280", line = "#9ca3af";
  return (
    <g data-testid="takeoff-legend" role="group" aria-label="Legend" transform={`translate(${pos.x},${pos.y})`}
      onPointerDown={startDrag} style={{ cursor: draggable ? "move" : "default" }} pointerEvents={draggable ? "all" : "none"}>
      <rect x={0} y={0} width={L.width} height={L.height} rx={fs * 0.3} fill="#ffffff" fillOpacity={0.96} stroke={ink} strokeWidth={fs * 0.07} />
      <text x={L.pad} y={L.pad + fs * 1.05} fontSize={fs * 1.05} fontWeight={700} fontFamily={SANS} fill={ink}>Legend</text>
      {slots.length > 0 && slots.map((i, k) => (
        <text key={i} x={L.colRight[k]} y={L.pad + L.titleH + fs * 0.85} fontSize={fs * 0.8} fontWeight={700} fontFamily={SANS} fill={faint} textAnchor="end">QTY {i + 1}</text>
      ))}
      <line x1={L.pad} x2={L.width - L.pad} y1={L.pad + L.titleH + L.headH - fs * 0.15} y2={L.pad + L.titleH + L.headH - fs * 0.15} stroke={line} strokeWidth={fs * 0.04} />
      {entries.length === 0 && <text x={L.pad} y={L.pad + L.titleH + fs * 1.1} fontSize={fs} fontFamily={SANS} fill={faint}>No conditions on this drawing.</text>}
      {L.rows.map(r => (
        <g key={r.entry.key}>
          <rect x={L.pad} y={r.y + L.lh * 0.5 - L.sw / 2} width={L.sw} height={L.sw} rx={fs * 0.12} fill={r.entry.color || "#94a3b8"} stroke={ink} strokeWidth={fs * 0.05} />
          <text x={L.labelX} y={r.y + L.lh * 0.5 + fs * 0.35} fontSize={fs} fontFamily={SANS} fill={ink}>{r.label}</text>
          {slots.map((i, k) => (
            <text key={i} x={L.colRight[k]} y={r.y + L.lh * 0.5 + fs * 0.35} fontSize={fs} fontFamily={MONO} fill={ink} textAnchor="end">{legendQtyText(r.entry.slots[i], units)}</text>
          ))}
        </g>
      ))}
    </g>
  );
}
